'use client';

import { useId, useMemo, useState } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { Badge } from '../../atoms/Badge/Badge';
import { Dropdown, DropdownOption } from '../../atoms/Dropdown/Dropdown';
import {
  CollapsiblePanel,
  CollapsiblePanelContent,
  CollapsiblePanelItem,
  CollapsiblePanelTrigger,
} from '../../molecules/CollapsiblePanel/CollapsiblePanel';
import { SheetPropsData } from '../../molecules/Sheets/Sheets.types';
import { SheetsStack } from '../../molecules/Sheets/SheetsStack';
import { ReleaseDetail } from '../ReleaseDetail/ReleaseDetail';
import {
  PackageRelease,
  PackageReleaseDetail,
  ReleaseListProps,
} from './ReleaseList.types';
import {
  isReleasesSortBy,
  RELEASES_SORT_BY_DEFAULT,
  sortReleases,
} from './sortReleases';

export const ReleaseList = ({
  packages,
  microcopy,
  sortBy = RELEASES_SORT_BY_DEFAULT,
  className = '',
  onAccordionChange,
  onSortChange,
}: ReleaseListProps) => {
  const sortIdPrefix = useId().replace(/:/g, '');
  const [selectedPackage, setSelectedPackage] = useState<DropdownOption | null>(
    packages.length
      ? { value: packages[0].title, label: packages[0].title }
      : null
  );

  const activePackage = useMemo(
    () =>
      selectedPackage
        ? (packages.find((p) => p.title === selectedPackage.value) ??
          packages[0])
        : packages[0],
    [packages, selectedPackage]
  );

  const dropdownOptions = useMemo(
    () => packages.map((pkg) => ({ value: pkg.title, label: pkg.title })),
    [packages]
  );

  const sortOptions = useMemo<DropdownOption[]>(
    () => [
      { value: 'version', label: microcopy.sortByVersion },
      { value: 'releaseDate', label: microcopy.sortByReleaseDate },
    ],
    [microcopy.sortByVersion, microcopy.sortByReleaseDate]
  );

  const selectedSortOption =
    sortOptions.find((option) => option.value === sortBy) ?? sortOptions[0];

  const sortedReleases = useMemo(
    () => sortReleases(activePackage?.releases ?? [], sortBy),
    [activePackage, sortBy]
  );

  const currentVersion = useMemo(
    () => activePackage?.releases.find((r) => !r.isDeprecated)?.version,
    [activePackage]
  );

  const activePackageAllDeprecated = useMemo(() => {
    if (!activePackage?.releases.length) return false;
    return activePackage.releases.every((r) => r.isDeprecated);
  }, [activePackage]);

  const renderReleaseListDropdown = (packages: PackageRelease[]) => {
    return (
      <>
        {packages.length > 1 && (
          <div className="mt-6 flex items-center gap-2 md:mt-0">
            <Dropdown
              id="release-list-package"
              options={dropdownOptions}
              selectedOption={selectedPackage}
              onSelect={(opt) => setSelectedPackage(opt)}
              isDarkTheme={false}
              className="flex-1"
            />
            {activePackageAllDeprecated && (
              <Badge
                text={microcopy.deprecated}
                data-test="package-deprecated-badge"
              />
            )}
          </div>
        )}
      </>
    );
  };

  const renderSortDropdown = (
    idSuffix: 'desktop' | 'mobile',
    className?: string
  ) => (
    <Dropdown
      id={`${sortIdPrefix}-release-list-sort-${idSuffix}`}
      options={sortOptions}
      selectedOption={selectedSortOption}
      onSelect={(opt) => {
        if (isReleasesSortBy(opt.value)) onSortChange(opt.value);
      }}
      label={microcopy.sortLabel}
      isDarkTheme={false}
      className={c(
        'text-sm [&_label]:font-normal [&_label]:text-content-subtle dark:[&_label]:text-content-subtle',
        className
      )}
    />
  );

  const renderReleaseLabel = (release: PackageReleaseDetail) => (
    <span className="flex items-center gap-2">
      {release.version}
      {release.version === currentVersion && (
        <span className="body-tiny text-grey-600">{microcopy.current}</span>
      )}
      {release.isDeprecated && <Badge text={microcopy.deprecated} />}
    </span>
  );

  const renderDesktopReleaseList = () => (
    <div className="hidden md:block">
      <div className="mb-8 flex items-start justify-between gap-4">
        <h2 className="text-md font-medium">{microcopy.title}</h2>
        {renderSortDropdown('desktop', 'w-auto shrink-0')}
      </div>

      {renderReleaseListDropdown(packages)}

      <CollapsiblePanel type="multiple" className="mt-8">
        {sortedReleases.map((release) => (
          <CollapsiblePanelItem
            key={`${activePackage.title}-${release.version ?? release.released}`}
            value={`${activePackage.title}-${release.version}`}
          >
            <CollapsiblePanelTrigger
              onValueChange={(value) => {
                onAccordionChange?.(
                  release.version || 'Unknown version',
                  value ? 'open' : 'close'
                );
              }}
            >
              {renderReleaseLabel(release)}
            </CollapsiblePanelTrigger>
            <CollapsiblePanelContent>
              <ReleaseDetail {...release} />
            </CollapsiblePanelContent>
          </CollapsiblePanelItem>
        ))}
      </CollapsiblePanel>
    </div>
  );

  const renderMobileReleaseList = () => {
    const mobileSheetDataWrapper: SheetPropsData[] = sortedReleases.map(
      (release) => ({
        label: renderReleaseLabel(release),
        children: <ReleaseDetail {...release} />,
        microcopy: microcopy.sheet,
      })
    );

    return (
      <div className="block md:hidden">
        {renderSortDropdown('mobile', 'mt-4')}
        {renderReleaseListDropdown(packages)}
        <SheetsStack
          sheets={mobileSheetDataWrapper}
          microcopy={microcopy.sheet}
          className={c('[&_p]:body-tiny mt-8 [&_p]:text-grey-700')}
        />
      </div>
    );
  };

  if (!packages.length) return null;

  return (
    <section className={c(className)}>
      {renderDesktopReleaseList()}
      {renderMobileReleaseList()}
    </section>
  );
};
